import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { useCanvasFiles } from '@/hooks/useCanvasFiles'

interface CanvasFileChoice {
  url: string
  name: string
  contentType?: string
  bytes?: number
}

/** Web File Manager pick: the canvas file library, not a second uploader. */
export function CanvasFileManagerDialog({ canvasId, multiple, onPick, onClose }: {
  canvasId: string
  multiple: boolean
  onPick: (files: CanvasFileChoice[]) => void
  onClose: () => void
}) {
  const { listFiles } = useCanvasFiles()
  const [files, setFiles] = useState<CanvasFileChoice[]>([])
  const [selected, setSelected] = useState<string[]>([])
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let live = true
    listFiles(canvasId, { limit: 100, sortBy: 'createdAt', sortOrder: 'desc' }).then(listed => {
      if (!live) return
      const images = listed
        .filter(file => file.publicUrl && file.contentType.startsWith('image/') && file.status !== 'DELETED')
        .map(file => ({ url: file.publicUrl as string, name: file.filename, contentType: file.contentType, bytes: file.size > 0 ? file.size : undefined }))
      setFiles(images)
      if (images.length === 0) setError('No images in File Manager yet. Upload one, or add a file to this canvas library.')
    }).catch(err => {
      if (live) setError(err instanceof Error ? err.message : 'File Manager could not be opened.')
    }).finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [canvasId, listFiles])

  useEffect(() => {
    const escape = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose() }
    window.addEventListener('keydown', escape)
    return () => window.removeEventListener('keydown', escape)
  }, [onClose])

  const toggle = (url: string) => {
    if (!multiple) {
      const file = files.find(item => item.url === url)
      if (file) onPick([file])
      return
    }
    setSelected(current => current.includes(url) ? current.filter(item => item !== url) : [...current, url])
  }

  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onMouseDown={event => { if (event.target === event.currentTarget) onClose() }}>
    <div role="dialog" aria-modal="true" aria-label="Choose from File Manager" className="flex max-h-[min(640px,90vh)] w-[min(560px,100%)] flex-col rounded-lg bg-popover p-3 text-popover-foreground">
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="text-sm font-medium">Choose from File Manager</p>
        <Button type="button" variant="ghost" size="sm" onClick={onClose}>Close</Button>
      </div>
      {loading && <p role="status" className="px-1 py-6 text-sm text-muted-foreground">Loading images…</p>}
      {error && <p role="alert" className="px-1 py-3 text-sm text-destructive">{error}</p>}
      <div className="grid min-h-0 flex-1 grid-cols-3 gap-2 overflow-y-auto">
        {files.map(file => {
          const pressed = selected.includes(file.url)
          return <button key={file.url} type="button" aria-pressed={multiple ? pressed : undefined} onClick={() => toggle(file.url)} className={`overflow-hidden rounded-md border text-left ${pressed ? 'border-foreground' : 'border-border'}`}>
            <img src={file.url} alt={file.name} className="h-24 w-full object-cover" />
            <span className="block truncate px-1.5 py-1 text-xs">{file.name}</span>
          </button>
        })}
      </div>
      {multiple && <div className="mt-2 flex justify-end">
        <Button type="button" size="sm" disabled={selected.length === 0} onClick={() => onPick(files.filter(file => selected.includes(file.url)))}>Use selected</Button>
      </div>}
    </div>
  </div>
}
